/**
 * Settings API Service
 * Handles user profile and currency settings communication with backend
 */

import supabase from '../supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  return {
    'Authorization': token ? `Bearer ${token}` : '',
  };
}

/**
 * Get user's profile with current settings
 */
export async function getUserProfile() {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/settings/profile`, {
    headers: authHeaders,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch profile');
  }

  return response.json();
}

/**
 * Update user profile (e.g., change default currency)
 */
export async function updateUserProfile(updates) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/settings/profile`, {
    method: 'PUT',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update profile');
  }

  return response.json();
}

/**
 * Get list of all supported currencies
 */
export async function getSupportedCurrencies() {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/settings/currencies`, {
    headers: authHeaders,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch currencies');
  }

  return response.json();
}

// ─── Telegram integration ───────────────────────────────────────────────────

/**
 * Get Telegram connection status
 */
export async function getTelegramStatus() {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/telegram/status`, {
    headers: authHeaders,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch Telegram status');
  }

  return response.json();
}

/**
 * Generate a Telegram deep-link token
 */
export async function generateTelegramLink() {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/telegram/link`, {
    method: 'POST',
    headers: authHeaders,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to generate Telegram link');
  }

  return response.json();
}

/**
 * Unlink Telegram account
 */
export async function unlinkTelegram() {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/telegram/unlink`, {
    method: 'DELETE',
    headers: authHeaders,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to unlink Telegram');
  }

  return response.json();
}

/**
 * Get Telegram conversation history
 */
export async function getTelegramHistory(limit = 50, offset = 0) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/telegram/history?limit=${limit}&offset=${offset}`, {
    headers: authHeaders,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch conversation history');
  }

  return response.json();
}

// ─── Auth / Connected Accounts ────────────────────────────────────────────────

/**
 * Get all connected auth providers for the current user
 */
export async function getConnectedAccounts() {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/auth/connected-accounts`, {
    headers: authHeaders,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch connected accounts');
  }

  return response.json();
}

/**
 * Link an email to the current user account
 * @param {string} email - Email to link
 * @param {string} token - Optional access token (used when called from App.js directly)
 * @returns {Object} { success, conflict?, conflictEmail?, mergeToken? }
 */
export async function linkEmail(email, token) {
  const headers = token
    ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { ...(await getAuthHeaders()), 'Content-Type': 'application/json' };

  const response = await fetch(`${API_URL}/api/auth/link-email`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ email }),
  });

  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.message || 'Failed to link email');
  }

  return result;
}

/**
 * Merge another account into the current user using a signed merge token
 * @param {string} mergeToken - Signed merge token from link-email conflict
 * @param {string} token - Optional access token
 */
export async function mergeAccounts(mergeToken, token) {
  const headers = token
    ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { ...(await getAuthHeaders()), 'Content-Type': 'application/json' };

  const response = await fetch(`${API_URL}/api/auth/merge-accounts`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ mergeToken }),
  });

  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.message || 'Failed to merge accounts');
  }

  return result;
}

// ─── Data & Account Deletion ──────────────────────────────────────────────────

/**
 * Selectively delete user data without deleting the account
 * @param {string[]} categories - Array of: 'receipts', 'expenses', 'email_forwarding', 'telegram'
 */
export async function deleteData(categories) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/auth/delete-data`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ categories }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete data');
  }

  return response.json();
}

/**
 * Delete the current user's account and all associated data
 */
export async function deleteAccount() {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/auth/delete-account`, {
    method: 'DELETE',
    headers: authHeaders,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete account');
  }

  return response.json();
}

// ─── Email Forwarding ─────────────────────────────────────────────────────────

/**
 * Get email forwarding status (whether enabled and the forwarding address)
 */
export async function getEmailForwardingStatus() {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/email-forwarding/status`, {
    headers: authHeaders,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch email forwarding status');
  }

  return response.json();
}

/**
 * Generate a new forwarding email address
 */
export async function generateEmailForwardingAddress() {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/email-forwarding/generate`, {
    method: 'POST',
    headers: authHeaders,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to generate forwarding address');
  }

  return response.json();
}

/**
 * Regenerate forwarding address (invalidates old one)
 */
export async function regenerateEmailForwardingAddress() {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/email-forwarding/regenerate`, {
    method: 'POST',
    headers: authHeaders,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to regenerate forwarding address');
  }

  return response.json();
}

/**
 * Get forwarded email history
 */
export async function getEmailForwardingHistory(limit = 20, offset = 0) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    `${API_URL}/api/email-forwarding/history?limit=${limit}&offset=${offset}`,
    { headers: authHeaders }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch forwarding history');
  }

  return response.json();
}

// ─── Admin Inbox ──────────────────────────────────────────────────────────────

/**
 * Get feedback inbox emails (admin only)
 */
export async function getInbox(limit = 50, offset = 0) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    `${API_URL}/api/admin/inbox?limit=${limit}&offset=${offset}`,
    { headers: authHeaders }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch inbox');
  }

  return response.json();
}

/**
 * Mark an inbox email as read (admin only)
 */
export async function markInboxRead(id) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/admin/inbox/${id}/read`, {
    method: 'PUT',
    headers: authHeaders,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to mark as read');
  }

  return response.json();
}

/**
 * Delete an inbox email (admin only)
 */
export async function deleteInboxEmail(id) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/admin/inbox/${id}`, {
    method: 'DELETE',
    headers: authHeaders,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete email');
  }

  return response.json();
}
